import {
  AlertTriangle,
  Bell,
  Check,
  CheckSquare,
  FileWarning,
  Gauge,
  Info,
  KeyRound,
  Lock,
  ShieldAlert,
  TrendingDown,
  User as UserIcon,
  Wallet,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { NotificationPriority } from '../contexts/NotificationsContext';

/**
 * Mapping `type` → icône + couleur d'icône (US-NOTIF-02 + 05).
 *
 * Le `type` est libre (chaîne) côté backend, mais l'inventaire est figé
 * dans US-NOTIF-04. Pour les types inconnus → fallback Bell gris.
 */

interface TypeStyle {
  icon: LucideIcon;
  /** Classe Tailwind appliquée à l'icône (foreground). */
  className: string;
}

const TYPE_STYLES: Record<string, TypeStyle> = {
  // Module Interventions
  task_assigned: { icon: CheckSquare, className: 'text-primary-600' },
  task_reassigned: { icon: CheckSquare, className: 'text-primary-600' },
  task_status_changed: { icon: Info, className: 'text-blue-600' },
  task_completed: { icon: Check, className: 'text-green-600' },
  task_deadline_approaching: { icon: AlertTriangle, className: 'text-orange-600' },
  validation_required: { icon: Check, className: 'text-green-600' },
  comment_added: { icon: Info, className: 'text-blue-600' },
  intervention_created: { icon: Wrench, className: 'text-primary-600' },

  // Module Équipements
  equipment_assignment_request: { icon: Bell, className: 'text-amber-600' },
  equipment_assignment_accepted: { icon: Check, className: 'text-green-600' },
  equipment_assignment_refused: { icon: X, className: 'text-red-600' },
  equipment_assignment_cancelled: { icon: X, className: 'text-gray-500' },
  maintenance_due: { icon: Wrench, className: 'text-orange-600' },
  calibration_due: { icon: Gauge, className: 'text-orange-600' },
  warranty_expiring: { icon: AlertTriangle, className: 'text-amber-600' },

  // Module Bâtiments
  reservation_pending_validation: { icon: Check, className: 'text-blue-600' },
  reservation_validated: { icon: Check, className: 'text-green-600' },
  reservation_refused: { icon: X, className: 'text-red-600' },
  document_expiring: { icon: FileWarning, className: 'text-amber-600' },

  // Module Administration
  user_created: { icon: UserIcon, className: 'text-primary-600' },
  role_changed: { icon: ShieldAlert, className: 'text-blue-600' },
  account_locked: { icon: Lock, className: 'text-red-600' },
  password_reset: { icon: KeyRound, className: 'text-blue-600' },

  // Module Services
  budget_threshold_reached: { icon: Wallet, className: 'text-orange-600' },
  budget_exceeded: { icon: TrendingDown, className: 'text-red-600' },
};

export function getNotificationTypeStyle(type: string): TypeStyle {
  return TYPE_STYLES[type] ?? { icon: Bell, className: 'text-gray-500' };
}

/**
 * Badge de priorité (US-NOTIF-05). Couleurs sémantiques Tailwind.
 */
export function getPriorityBadgeClass(priority: NotificationPriority): string {
  switch (priority) {
    case 'urgent':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'high':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}

export function getPriorityLabel(priority: NotificationPriority): string {
  switch (priority) {
    case 'urgent':
      return 'Urgent';
    case 'high':
      return 'Haute';
    default:
      return 'Normal';
  }
}

/**
 * Groupement temporel pour le dropdown (Aujourd'hui / Hier / Cette semaine /
 * Plus ancien).
 */
export function getDateGroup(date: Date): 'today' | 'yesterday' | 'thisWeek' | 'older' {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 6);

  if (date >= startOfToday) return 'today';
  if (date >= startOfYesterday) return 'yesterday';
  if (date >= startOfWeek) return 'thisWeek';
  return 'older';
}

export const DATE_GROUP_LABEL: Record<ReturnType<typeof getDateGroup>, string> = {
  today: "Aujourd'hui",
  yesterday: 'Hier',
  thisWeek: 'Cette semaine',
  older: 'Plus ancien',
};

/** Date relative en français ("il y a 3 min"). */
export function relativeTime(input: string | Date): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (diffSec < 30) return "à l'instant";
  if (diffSec < 60) return `il y a ${diffSec} s`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH} h`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `il y a ${diffD} j`;
  return date.toLocaleDateString();
}
